import React, { FC, useState } from 'react';

import {
    Button,
    ModalBody,
    ModalFooter,
    ModalHeader,
    FileUploader,
    Modal
} from 'common/components';
import { ButtonVariant, EntityType, ModalSize } from 'common/enums';
import { FieldError, FileDetails } from 'common/models';
import { ACTION_ITEMS, FILE_COUNT_ERROR, FILE_COUNT_MAX, FILE_TYPE_ERROR } from 'common/config';

import './AddIndicatorMaster.scss';
import DownloadTemplate from '../download-template/DownloadTemplate';
import { useDispatch } from 'react-redux';
import { fileUploadActions } from 'common/redux/file-upload';
import { isAllowedFileType } from 'common/utils/file';
import { FileUploadVariants } from 'common/enums/variants.enum';

type AddIndicatorMasterProps = {
    subCategoryId: string;
    isVisible: boolean;
    uploadingFiles: Array<FileDetails>;
    selectedFiles: Array<FileDetails>;
    onSelectedFileRemove: (file: FileDetails) => void;
    onClose: () => void;
    onUpload: () => void;
};

const AddIndicatorMaster: FC<AddIndicatorMasterProps> = ({
    subCategoryId,
    uploadingFiles,
    selectedFiles,
    isVisible,
    onSelectedFileRemove,
    onUpload,
    onClose
}) => {
    const [uploadError, setUploadError] = useState<string>('');

    const dispatch = useDispatch();

    const handleFileDrop = (files: Array<FileDetails>) => {
        let isAllowedType = true;

        setUploadError('');
        dispatch(fileUploadActions.setSelectedFiles([]));

        if (files.length > FILE_COUNT_MAX) {
            setUploadError(FILE_COUNT_ERROR);
        } else {
            const newFiles = files.map((item: FileDetails) => {
                item.targetID = subCategoryId;
                isAllowedType = isAllowedFileType(item, /(\.xls|\.xlsx)$/i);
                return item;
            });
            if (isAllowedType) {
                dispatch(fileUploadActions.setSelectedFiles(newFiles));
            } else {
                setUploadError(FILE_TYPE_ERROR);
            }
        }
    };

    const handleUpload = () => {
        dispatch(
            fileUploadActions.uploadFile({
                files: selectedFiles,
                variantName: FileUploadVariants.MasterData,
                targetId: subCategoryId,
                entityType: EntityType.Indicator
            })
        );
        onUpload();
    };

    const handleClose = () => {
        setUploadError('');
        onClose();
    };

    return (
        <Modal
            show={isVisible}
            onClose={onClose}
            size={ModalSize.Large}
            className="add-indicator-modal">
            <ModalHeader className="category-add-header">{'Add new indicator'}</ModalHeader>
            <ModalBody className="category-add-body">
                <div className="category-body-header">
                    <DownloadTemplate fromMaster={true} />
                </div>
                <FileUploader
                    maxFiles={FILE_COUNT_MAX}
                    uploadingFiles={uploadingFiles}
                    selectedFiles={selectedFiles}
                    errorMessage={uploadError}
                    onSelectedFileRemove={onSelectedFileRemove}
                    onDrop={handleFileDrop}
                />
            </ModalBody>
            <ModalFooter className="category-add-footer">
                <Button
                    variant={ButtonVariant.Primary}
                    onClick={handleUpload}
                    disabled={selectedFiles?.length < 1}>
                    {ACTION_ITEMS.SAVE_BUTTON}
                </Button>
                <Button variant={ButtonVariant.Secondary} onClick={handleClose}>
                    {ACTION_ITEMS.CANCEL_BUTTON}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default AddIndicatorMaster;
